<ng-template #moderationIcon>
  <svg-icon
    src="/assets/icons/new-moderation.svg"
    class="moderation-icon"
    [matTooltip]="'label.IsInModeration' | translate"
  ></svg-icon>
</ng-template>

<ng-template #downloadCvButton let-disabled="disabled">
  <button class="download-cv" [disabled]="disabled" (click)="onCvExport()">
    <svg-icon src="/assets/icons/profile-download.svg" class="download-cv__icon"></svg-icon>
    {{ 'label.DownloadCv' | translate }}
  </button>
</ng-template>

<ng-template #addButton>
  <span class="add-button__icon">
    <svg-icon src="/assets/icons/profile-plus.svg"></svg-icon>
  </span>
  {{ 'label.Add' | translate }}
</ng-template>

<ng-template #editIcon>
  <svg-icon src="/assets/icons/profile-pencil.svg"></svg-icon>
</ng-template>

<ng-template #deleteIcon>
  <svg-icon src="/assets/icons/profile-trash.svg"></svg-icon>
</ng-template>

<ng-template #attachmentIcon>
  <svg-icon src="/assets/icons/profile-attachment.svg" class="attachment-link__icon"></svg-icon>
</ng-template>

<app-spinner [isLoading]="isLoading$ | async">
  @if (
    {
      year: workExperienceYears$ | async,
      month: workExperienceMonth$ | async,
      cvExportLoading: cvExportLoading$ | async,
      jobseekerWorkExperiences: jobseekerWorkExperiences$ | async,
      jobseekerPersonalInfo: jobseekerPersonalInfo$ | async,
      jobseekerEducations: jobseekerEducations$ | async,
      documents: documents$ | async,
      documentInMod: documentInMod$ | async,
      educationMaxLevel: educationMaxLevel$ | async,
      languagesInMod: languagesInMod$ | async,
      jobseekerLanguages: jobseekerLanguages$ | async,
      jobseekerHardSkills: jobseekerHardSkills$ | async,
      jobseekerSoftSkills: jobseekerSoftSkills$ | async,
      photoIsInModeration: photoIsInModeration$ | async,
      jobseekerNewExperiences: jobseekerPendingExperiences$ | async,
      jobseekerPendingExperiences: jobseekerNewExperiences$ | async,
      documentTypeMap: documentTypeMap$ | async,
      currentLanguage: currentLanguage$ | async,
      isWorkExperienceFilledInModeration: isWorkExperienceFilledInModeration$ | async,
      hardSkillsInModeration: hardSkillsInModeration$ | async,
      softSkillsInModeration: softSkillsInModeration$ | async
    };
    as data
  ) {
    <div class="profile-page">
      @if (jobseeker$ | async; as jobseeker) {
        @let info = data.jobseekerPersonalInfo;
        <div class="profile-content">
          <div class="profile-toolbar">
            @if (jobseeker.lacksNecessaryInformation) {
              <div class="lack-info">
                <svg-icon class="lack-info__icon" src="/assets/icons/warning.svg"></svg-icon>
                <p class="lack-info__text">
                  {{ 'message.JobseekerProfileLacksNecessaryInformation' | translate }}
                  @for (lackItem of jobseeker.lackedFieldList; track lackItem; let last = $last) {
                    <span class="lack-info__item">{{ necessaryFieldMap[lackItem] | translate }}{{ last ? '' : '; ' }}</span>
                  }
                </p>
              </div>
            }
            @if (showCvExport) {
              <ng-container *ngTemplateOutlet="downloadCvButton; context: { disabled: data.cvExportLoading }"></ng-container>
            }
          </div>
          @if (data.cvExportLoading) {
            <mat-progress-bar class="profile-progress" mode="indeterminate"></mat-progress-bar>
          }

          <app-profile-section-tile [title]="'label.PersonalInformation' | translate" headerSpacing="none">
            <div tile-actions class="tile-actions">
              <button class="icon-button" [attr.aria-label]="'label.Edit' | translate" [matTooltip]="'label.Edit' | translate" (click)="editPersonalInfo()">
                <ng-container *ngTemplateOutlet="editIcon"></ng-container>
              </button>
            </div>

            <div class="personal">
              <div class="personal__info">
                <div class="personal__name-row">
                  <h1 class="personal__name">{{ info?.name }} {{ info?.surname }}</h1>
                  @if (info?.nameIsInModeration) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                </div>

                <ul class="personal__list">
                  <li class="personal__item">
                    <span class="personal__icon"><svg-icon src="/assets/icons/profile-mail.svg"></svg-icon></span>
                    <span>{{ info?.email }}</span>
                    @if (info?.emailIsInModeration) {
                      <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                    }
                  </li>
                  <li class="personal__item">
                    <span class="personal__icon"><svg-icon src="/assets/icons/profile-phone.svg"></svg-icon></span>
                    <span>{{ info?.phone }}{{ info?.phone2 ? '; ' + info.phone2 : '' }}</span>
                    @if (info?.phoneIsInModeration) {
                      <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                    }
                  </li>
                  @if (info?.gender) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-gender.svg"></svg-icon></span>
                      <span>{{ 'label.Gender' | translate }}: {{ info.gender }}</span>
                      @if (info.genderIsInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                  <li class="personal__item">
                    <span class="personal__icon"><svg-icon src="/assets/icons/profile-calendar.svg"></svg-icon></span>
                    <span>
                      {{ 'label.DateOfBirth' | translate }}:
                      {{ info?.birthDate ? (info.birthDate | date: 'dd MMMM y' : null : data.currentLanguage) : ('label.NotSelected' | translate) }}
                    </span>
                    @if (info?.birthDateIsInModeration) {
                      <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                    }
                  </li>
                  @if (info?.jobseekerExtendedLocations?.length) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-location.svg"></svg-icon></span>
                      <span>{{ 'label.CurrentLocation' | translate }}: {{ info.jobseekerExtendedLocations.join(', ') }}</span>
                      @if (info.isExtendedLocationInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                  @if (info?.address) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-location.svg"></svg-icon></span>
                      <span>{{ 'label.ExactAddress' | translate }}: {{ info.address }}</span>
                      @if (info.addressIsInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                  @if (info?.legalAddress) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-location.svg"></svg-icon></span>
                      <span>{{ 'label.LegalAddress' | translate }}: {{ info.legalAddress }}</span>
                      @if (info.legalAddressIsInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                  @if (info?.drivingLicenses?.length) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-license.svg"></svg-icon></span>
                      <span>{{ 'label.DrivingLicence' | translate }}: {{ info.drivingLicenses.join(', ') }}</span>
                      @if (info.drivingLicenseIsInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                  <li class="personal__item">
                    <span class="personal__icon"><svg-icon src="/assets/icons/profile-car.svg"></svg-icon></span>
                    <span>{{ 'label.OwnCar' | translate }}: {{ (info?.isVehicleOwner ? 'label.Yes' : 'label.No') | translate }}</span>
                    @if (info?.ownVehicleIsInModeration) {
                      <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                    }
                  </li>
                  @if (info?.jobseekerVehicles?.length) {
                    <li class="personal__item">
                      <span class="personal__icon"><svg-icon src="/assets/icons/profile-car.svg"></svg-icon></span>
                      <span>
                        {{ 'label.TransportType' | translate }}:
                        @for (vehicle of info.jobseekerVehicles; track vehicle.vehicleName; let last = $last) {
                          {{ vehicle.vehicleName }}{{ last ? '' : ', ' }}
                        }
                      </span>
                      @if (info.transportTypeIsInModeration) {
                        <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                      }
                    </li>
                  }
                </ul>
              </div>

              <div class="photo" [class.photo--empty]="!jobseeker.photoHref">
                @if (jobseeker.photoHref) {
                  <img class="photo__img" [src]="jobseeker.photoHref" alt="" />
                  @if (!data.photoIsInModeration) {
                    <div class="photo__overlay">
                      <button class="photo__action" (click)="editPhoto(false, jobseeker.photoHref)">
                        <svg-icon src="/assets/icons/profile-photo-edit.svg"></svg-icon>
                        {{ 'label.EditPhoto' | translate }}
                      </button>
                      <button class="photo__action photo__action--danger" (click)="deletePhoto(jobseeker.moderationLastChangeDate)">
                        <svg-icon src="/assets/icons/profile-photo-delete.svg"></svg-icon>
                        {{ 'label.DeletePhoto' | translate }}
                      </button>
                    </div>
                  }
                } @else if (!data.photoIsInModeration) {
                  <button class="photo__add" (click)="editPhoto(false, jobseeker.photoHref)">{{ 'label.AddPhoto' | translate }}</button>
                }
                @if (data.photoIsInModeration) {
                  <span class="photo__moderation">
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  </span>
                }
              </div>
            </div>
          </app-profile-section-tile>

          <app-profile-section-tile
            [title]="'label.WorkExperience' | translate"
            [rightText]="('label.Year' | translate: { count: data.year }) + ' ' + ('label.Month' | translate: { count: data.month })"
          >
            <div tile-actions class="tile-actions">
              @if (!jobseeker.isWorkExperienceFilled) {
                <button class="text-button" (click)="onIsWorkExperienceFilled(jobseeker.moderationLastChangeDate)">
                  {{ 'label.WithoutExperience' | translate }}
                </button>
              }
              <button class="add-button" (click)="onWorkExperienceCreate()">
                <ng-container *ngTemplateOutlet="addButton"></ng-container>
              </button>
            </div>

            @for (experience of data.jobseekerWorkExperiences; track experience.experienceId) {
              <div class="entry">
                <div class="entry__body">
                  <div class="entry__headline">
                    <span class="entry__title">{{ experience.positionTitle }}</span>
                    <span class="entry__text">
                      {{ experience.dateFromMonth == null ? experience.dateFromYear : (experience.experienceDateFrom | date: 'y MMMM' : null : data.currentLanguage) }}
                      -
                      @if (experience.isCurrent) {
                        {{ 'label.Present' | translate }}
                      } @else {
                        {{ experience.dateToMonth == null ? experience.dateToYear : (experience.experienceDateTo | date: 'y MMMM' : null : data.currentLanguage) }}
                      }
                    </span>
                    <span class="entry__muted">
                      @if (experience.workExperienceDuration.year !== 0) {
                        {{ 'label.Year' | translate: { count: experience.workExperienceDuration.year } }}
                      }
                      @if (experience.workExperienceDuration.month !== 0) {
                        {{ 'label.Month' | translate: { count: experience.workExperienceDuration.month } }}
                      }
                    </span>
                  </div>

                  <div class="entry__meta">
                    @if (experience.seniorityLevelName) {
                      <span class="entry__meta-item" [matTooltip]="'label.SeniorityLevel' | translate">
                        <svg-icon src="/assets/icons/profile-seniority.svg"></svg-icon>{{ experience.seniorityLevelName }}
                      </span>
                    }
                    @if (experience.employmentFormTypeName) {
                      <span class="entry__meta-item" [matTooltip]="'label.EmploymentLocation' | translate">
                        <svg-icon src="/assets/icons/profile-hybrid.svg"></svg-icon>{{ experience.employmentFormTypeName }}
                      </span>
                    }
                    @if (experience.employmentFormName) {
                      <span class="entry__meta-item" [matTooltip]="'label.EmploymentType' | translate">
                        <svg-icon src="/assets/icons/profile-contract.svg"></svg-icon>{{ experience.employmentFormName }}
                      </span>
                    }
                    @if (experience.workScheduleName) {
                      <span class="entry__meta-item" [matTooltip]="'label.WorkSchedule' | translate">
                        <svg-icon src="/assets/icons/profile-schedule.svg"></svg-icon>{{ experience.workScheduleName }}
                      </span>
                    }
                  </div>

                  @if (experience.extendedLocations?.length) {
                    <div class="entry__location">
                      <svg-icon src="/assets/icons/profile-location.svg"></svg-icon>
                      {{ experience.extendedLocations.join(', ') }}
                    </div>
                  }
                  <p class="entry__company">{{ experience.employer }}</p>
                  @if (experience.description) {
                    <p class="entry__description">{{ experience.description }}</p>
                  }
                  @if (experience.groupedSpecializations?.length) {
                    <p class="entry__category">
                      <span class="entry__category-label">{{ 'label.SpecializationPosition' | translate }}:</span>
                      @for (group of experience.groupedSpecializations; track group.head; let lastGroup = $last) {
                        {{ group.head }} ›
                        @for (child of group.subSpecializations; track child.name; let last = $last) {
                          {{ child.name }}{{ last ? (lastGroup ? '' : ';') : ',' }}
                        }
                      }
                    </p>
                  }
                  @if (experience.groupedIndustries?.length) {
                    <p class="entry__category">
                      <span class="entry__category-label">{{ 'label.IndustryField' | translate }}:</span>
                      @for (group of experience.groupedIndustries; track group.head; let lastGroup = $last) {
                        {{ group.head }} ›
                        @for (child of group.subindustries; track child; let last = $last) {
                          {{ child }}{{ last ? (lastGroup ? '' : ';') : ',' }}
                        }
                      }
                    </p>
                  }
                  @if (experience.fileName != null) {
                    <button class="attachment-link" (click)="downloadExperienceAttachment(experience.experienceId)">
                      <ng-container *ngTemplateOutlet="attachmentIcon"></ng-container>
                      {{ 'label.Attachment' | translate }}
                    </button>
                  }
                </div>
                <div class="entry__actions">
                  @if (isExperienceInModeration(data.jobseekerNewExperiences, data.jobseekerPendingExperiences, experience.experienceId)) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                  <button class="icon-button" [attr.aria-label]="'label.Edit' | translate" [matTooltip]="'label.Edit' | translate" (click)="editWorkExperience(experience)">
                    <ng-container *ngTemplateOutlet="editIcon"></ng-container>
                  </button>
                  <button
                    class="icon-button"
                    [attr.aria-label]="'label.Delete' | translate"
                    [matTooltip]="'label.Delete' | translate"
                    (click)="deleteExperience({ id: experience.experienceId, objectId: experience.objectId }, jobseeker.moderationLastChangeDate)"
                  >
                    <ng-container *ngTemplateOutlet="deleteIcon"></ng-container>
                  </button>
                </div>
              </div>
            }
            @if (!data.jobseekerWorkExperiences?.length && jobseeker.isWorkExperienceFilled) {
              <p class="entry__text entry__text--with-icon">
                {{ 'label.WithoutExperience' | translate }}
                @if (data.isWorkExperienceFilledInModeration) {
                  <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                }
              </p>
            }
          </app-profile-section-tile>

          <app-profile-section-tile [title]="'label.Education' | translate" [rightText]="data.educationMaxLevel">
            <div tile-actions class="tile-actions">
              <button class="add-button" (click)="onEducationCreate()">
                <ng-container *ngTemplateOutlet="addButton"></ng-container>
              </button>
            </div>

            @for (education of data.jobseekerEducations; track $index) {
              <div class="entry">
                <div class="entry__body">
                  <div class="entry__headline entry__headline--wide">
                    <span class="entry__title">{{ education.educationLevel.name }}</span>
                    @if ((education.hasProgram && education.institution) || (education.hasSpecialization && education.specialization)) {
                      <span class="entry__text">
                        {{ education.hasProgram ? education.institution : '' }}
                        {{ education.hasProgram && education.institution && education.hasSpecialization && education.specialization ? '|' : '' }}
                        {{ education.hasSpecialization ? education.specialization : '' }}
                      </span>
                    }
                    <span class="entry__text">
                      {{ education.dateFromMonth == null ? education.dateFromYear : (education.educationDateFrom | date: 'y MMMM' : null : data.currentLanguage) }}
                      -
                      {{ education.dateToMonth == null ? education.dateToYear : (education.educationDateTo | date: 'y MMMM' : null : data.currentLanguage) }}
                    </span>
                  </div>
                  <p class="entry__institution">{{ education.educationalProgramName }}</p>
                  @if (education.description) {
                    <p class="entry__note">{{ education.description }}</p>
                  }
                  @if (education.attachment != null) {
                    <button class="attachment-link" (click)="downloadEducationAttachment(education.objectId)">
                      <ng-container *ngTemplateOutlet="attachmentIcon"></ng-container>
                      {{ 'label.Attachment' | translate }}
                    </button>
                  }
                </div>
                <div class="entry__actions">
                  @if (education.isInModeration) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                  <button class="icon-button" [attr.aria-label]="'label.Edit' | translate" [matTooltip]="'label.Edit' | translate" (click)="editEducation(education.objectId)">
                    <ng-container *ngTemplateOutlet="editIcon"></ng-container>
                  </button>
                  <button
                    class="icon-button"
                    [attr.aria-label]="'label.Delete' | translate"
                    [matTooltip]="'label.Delete' | translate"
                    (click)="deleteEducation({ id: education.jobseekerEducationId, objectId: education.objectId }, jobseeker.moderationLastChangeDate)"
                  >
                    <ng-container *ngTemplateOutlet="deleteIcon"></ng-container>
                  </button>
                </div>
              </div>
            }
          </app-profile-section-tile>

          <app-profile-section-tile [title]="'label.Skills' | translate" headerSpacing="none">
            <ng-container
              *ngTemplateOutlet="skillGroup; context: { title: 'label.HardSkills', skills: data.jobseekerHardSkills, inModeration: data.hardSkillsInModeration, type: skillBase.Hard }"
            ></ng-container>
            <ng-container
              *ngTemplateOutlet="skillGroup; context: { title: 'label.SoftSkills', skills: data.jobseekerSoftSkills, inModeration: data.softSkillsInModeration, type: skillBase.Soft }"
            ></ng-container>
          </app-profile-section-tile>

          <ng-template #skillGroup let-title="title" let-skills="skills" let-inModeration="inModeration" let-type="type">
            <div class="skill-group">
              <div class="skill-group__header">
                <span class="skill-group__title">
                  {{ title | translate }}
                  @if (inModeration) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                </span>
                <div class="tile-actions">
                  <button class="add-button" (click)="onSkillsEdit(type)">
                    <ng-container *ngTemplateOutlet="addButton"></ng-container>
                  </button>
                  @if (skills?.length) {
                    <button class="icon-button" [attr.aria-label]="'label.Edit' | translate" [matTooltip]="'label.Edit' | translate" (click)="onSkillsEdit(type)">
                      <ng-container *ngTemplateOutlet="editIcon"></ng-container>
                    </button>
                  }
                </div>
              </div>
              @if (skills?.length) {
                <div class="skill-group__chips">
                  @for (skill of skills; track $index) {
                    <span class="skill-chip" [class.skill-chip--moderation]="!skill.name">{{ skill.name || skill.userProvidedText }}</span>
                  }
                </div>
              }
            </div>
          </ng-template>

          <app-profile-section-tile [title]="'label.Languages' | translate" headerSpacing="dense">
            <div tile-actions class="tile-actions">
              <button class="add-button" (click)="onLanguageCreate()">
                <ng-container *ngTemplateOutlet="addButton"></ng-container>
              </button>
            </div>

            @for (language of data.jobseekerLanguages; track language.jobseekerLanguageId) {
              <div class="row">
                <div class="row__cells">
                  <span class="row__name">{{ language.worldLanguage.name }}</span>
                  <span class="row__value">{{ language.languageProficiencyLevel.name }}</span>
                  @if (language.attachment) {
                    <button class="attachment-link" (click)="downloadLanguageAttachment(language.objectId)">
                      <ng-container *ngTemplateOutlet="attachmentIcon"></ng-container>
                      {{ 'label.Attachment' | translate }}
                    </button>
                  }
                </div>
                <div class="entry__actions">
                  @if (data.languagesInMod[language.jobseekerLanguageId]) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                  <button
                    class="icon-button"
                    [attr.aria-label]="'label.Edit' | translate"
                    [matTooltip]="'label.Edit' | translate"
                    (click)="editLanguage({ id: language.jobseekerLanguageId, objectId: language.objectId })"
                  >
                    <ng-container *ngTemplateOutlet="editIcon"></ng-container>
                  </button>
                  <button
                    class="icon-button"
                    [attr.aria-label]="'label.Delete' | translate"
                    [matTooltip]="'label.Delete' | translate"
                    (click)="deleteLanguage({ id: language.jobseekerLanguageId, objectId: language.objectId }, jobseeker.moderationLastChangeDate)"
                  >
                    <ng-container *ngTemplateOutlet="deleteIcon"></ng-container>
                  </button>
                </div>
              </div>
            }
          </app-profile-section-tile>

          <app-profile-section-tile [title]="'label.Documents' | translate" headerSpacing="dense">
            <div tile-actions class="tile-actions">
              <button class="add-button" (click)="onDocumentCreate()">
                <ng-container *ngTemplateOutlet="addButton"></ng-container>
              </button>
            </div>

            @if (data.documents?.length) {
              <div class="row row--documents row--head">
                <div class="row__cells">
                  <span class="row__document">{{ 'label.Document' | translate }}</span>
                  <span>{{ 'label.UploadDate' | translate }}</span>
                </div>
              </div>
            }
            @for (document of data.documents; track document.jobseekerDocumentId) {
              <div class="row row--documents">
                <div class="row__cells">
                  <button class="attachment-link attachment-link--large row__document" (click)="downloadDocument(document.objectId)">
                    <ng-container *ngTemplateOutlet="attachmentIcon"></ng-container>
                    {{ data.documentTypeMap[document.type]?.name }}
                  </button>
                  <span class="row__value">{{ document.createDate | date: 'shortDate' }}</span>
                  @if (document.label) {
                    <span class="row__muted">{{ document.label }}</span>
                  }
                </div>
                <div class="entry__actions">
                  @if (data.documentInMod[document.jobseekerDocumentId]) {
                    <ng-container *ngTemplateOutlet="moderationIcon"></ng-container>
                  }
                  <button
                    class="icon-button"
                    [attr.aria-label]="'label.Delete' | translate"
                    [matTooltip]="'label.Delete' | translate"
                    (click)="deleteDocument({ id: document.jobseekerDocumentId, objectId: document.objectId }, jobseeker.moderationLastChangeDate)"
                  >
                    <ng-container *ngTemplateOutlet="deleteIcon"></ng-container>
                  </button>
                </div>
              </div>
            }
          </app-profile-section-tile>

          @if (showCvExport) {
            <div class="profile-footer">
              <ng-container *ngTemplateOutlet="downloadCvButton; context: { disabled: data.cvExportLoading }"></ng-container>
            </div>
          }
        </div>
      }
    </div>
  }
</app-spinner>
